/**
 * AI Design Library — shared localStorage layer used by both the designer
 * page (ai-designer.js) and the gallery page (my-designs.js).
 *
 * Storage shape (under key "ai_design_library_v1"):
 *   { version: 1, designs: [
 *       { id, prompt, draftUrl, thumbnail, createdAt, lastUsedAt }
 *   ] }
 *
 *   - id          — uuid string
 *   - prompt      — what the customer typed
 *   - draftUrl    — R2 URL of the 1024 draft PNG
 *   - thumbnail   — small (~10–20 KB) base64 JPEG used in the gallery
 *   - createdAt   — ms epoch when generated
 *   - lastUsedAt  — ms epoch when last downloaded / used (for sorting later)
 *
 * Cap: 30 designs. When over cap, oldest by createdAt is dropped first.
 */
(function (global) {
  // Guard against double-load. This script is referenced from multiple
  // liquid snippets (sections + ai-design-attach + ai-promo-meta) so the
  // IIFE evaluates twice on pages that include more than one of them. A
  // second eval would replace global.AIDesignLibrary with a fresh closure
  // — wiping any state already set (worker URL, magic-link bind, etc.)
  // and causing later cloud calls to silently no-op.
  if (global.AIDesignLibrary && global.AIDesignLibrary._v >= 2) return;

  const STORAGE_KEY = "ai_design_library_v1";
  const MAX_DESIGNS = 30;

  // ============= Cloud sync — keys + config =============
  // The cloud layer optionally backs the local library with a server-side copy
  // tied to a customer's email address. Once the customer hands over their
  // email, we get a magic-link token; that token lets us push/pull designs
  // to /api/library/* on the Worker.
  const TOKEN_KEY = "ai_library_token";        // string — magic-link token
  const EMAIL_KEY = "ai_library_email";        // string — last bound email (display only)
  const BANNER_KEY = "ai_library_banner_state"; // "dismissed" | "subscribed"
  const SYNCED_KEY = "ai_library_synced_ids";  // JSON Set of design IDs already pushed
  // Worker URL is resolved lazily on first use. ai-designer.js or any page
  // that includes the library can call AIDesignLibrary.cloud.setWorkerUrl().
  let cloudWorkerUrl = "";

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { version: 1, designs: [] };
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.designs)) {
        return { version: 1, designs: [] };
      }
      return parsed;
    } catch (e) {
      console.warn("AIDesignLibrary read failed", e);
      return { version: 1, designs: [] };
    }
  }

  function write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (e) {
      // QuotaExceededError — typically when localStorage is full.
      // Try evicting half the designs and retry once.
      console.warn("AIDesignLibrary write failed, attempting eviction", e);
      if (state.designs.length > 1) {
        state.designs = state.designs.slice(0, Math.floor(state.designs.length / 2));
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
          return true;
        } catch (_) {
          return false;
        }
      }
      return false;
    }
  }

  function uuid() {
    if (global.crypto && global.crypto.randomUUID) return global.crypto.randomUUID();
    // Fallback for older browsers.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function list() {
    return read().designs.slice().sort(function (a, b) {
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  }

  function get(id) {
    return read().designs.find(function (d) { return d.id === id; }) || null;
  }

  function save(input) {
    const state = read();
    const now = Date.now();
    const design = {
      id: uuid(),
      prompt: String(input.prompt || ""),
      draftUrl: String(input.draftUrl || ""),
      thumbnail: input.thumbnail ? String(input.thumbnail) : "",
      createdAt: now,
      lastUsedAt: now,
    };
    state.designs.push(design);
    // Cap by oldest createdAt
    if (state.designs.length > MAX_DESIGNS) {
      state.designs.sort(function (a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
      state.designs = state.designs.slice(0, MAX_DESIGNS);
    }
    write(state);
    // Mirror to cloud if signed in. Fire-and-forget; network failures don't
    // break local saving — the next sync pass will pick it up.
    if (cloudGetToken()) {
      cloudAppend(design).catch(function (e) {
        console.warn("AIDesignLibrary cloud append failed", e);
      });
    }
    return design;
  }

  function remove(id) {
    const state = read();
    const idx = state.designs.findIndex(function (d) { return d.id === id; });
    if (idx === -1) return false;
    state.designs.splice(idx, 1);
    write(state);
    return true;
  }

  function touch(id) {
    const state = read();
    const design = state.designs.find(function (d) { return d.id === id; });
    if (!design) return false;
    design.lastUsedAt = Date.now();
    write(state);
    return true;
  }

  function clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (_) {
      return false;
    }
  }

  // ============= Cloud sync — implementation =============
  function cloudSetWorkerUrl(url) {
    if (typeof url === "string" && url) cloudWorkerUrl = url.replace(/\/$/, "");
  }
  function cloudGetWorkerUrl() {
    return cloudWorkerUrl;
  }
  function cloudGetToken() {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; }
  }
  function cloudGetEmail() {
    try { return localStorage.getItem(EMAIL_KEY) || ""; } catch (_) { return ""; }
  }
  function cloudIsSignedIn() {
    return !!cloudGetToken();
  }
  function cloudBind(email, token) {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, String(token));
      if (email) localStorage.setItem(EMAIL_KEY, String(email).toLowerCase());
    } catch (_) {}
  }
  function cloudSignOut() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EMAIL_KEY);
      localStorage.removeItem(SYNCED_KEY);
    } catch (_) {}
  }

  function readSyncedIds() {
    try {
      const raw = localStorage.getItem(SYNCED_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) { return {}; }
  }
  function writeSyncedIds(map) {
    try { localStorage.setItem(SYNCED_KEY, JSON.stringify(map)); } catch (_) {}
  }
  function markSynced(id) {
    if (!id) return;
    const m = readSyncedIds();
    m[id] = 1;
    writeSyncedIds(m);
  }

  /**
   * First-time email capture. Sends the current local library + the email
   * to the worker. Worker mints a token, fires Klaviyo email, returns token.
   * We store the token + email locally and remember which IDs were uploaded.
   */
  async function cloudSave(email) {
    if (!cloudWorkerUrl) throw new Error("Cloud library not configured.");
    const designs = list().map(function (d) {
      return {
        id: d.id,
        draftUrl: d.draftUrl,
        prompt: d.prompt || "",
        thumbnail: d.thumbnail || "",
        createdAt: d.createdAt || Date.now(),
        lastUsedAt: d.lastUsedAt || d.createdAt || Date.now(),
      };
    });
    const resp = await fetch(cloudWorkerUrl + "/api/library/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email, designs: designs }),
    });
    let payload = null;
    try { payload = await resp.json(); } catch (_) { payload = null; }
    if (!resp.ok || !payload || !payload.ok) {
      const msg = (payload && payload.error) || ("Save failed (" + resp.status + ")");
      throw new Error(msg);
    }
    cloudBind(email, payload.token || "");
    // Mark every uploaded design as already-synced so the next append pass
    // doesn't re-push them.
    designs.forEach(function (d) { markSynced(d.id); });
    return payload; // { ok, designCount, emailed, token }
  }

  /** Append a single newly-created design to the cloud library. */
  async function cloudAppend(design) {
    if (!cloudWorkerUrl) return null;
    const token = cloudGetToken();
    if (!token) return null;
    const synced = readSyncedIds();
    if (synced[design.id]) return null; // already pushed
    const resp = await fetch(cloudWorkerUrl + "/api/library/append", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: token,
        design: {
          id: design.id,
          draftUrl: design.draftUrl,
          prompt: design.prompt || "",
          thumbnail: design.thumbnail || "",
          createdAt: design.createdAt || Date.now(),
          lastUsedAt: design.lastUsedAt || design.createdAt || Date.now(),
        },
      }),
    });
    let payload = null;
    try { payload = await resp.json(); } catch (_) { payload = null; }
    if (!resp.ok || !payload || !payload.ok) {
      // Token expired or revoked — clear local auth state so the customer
      // can re-bind. Don't throw; this is a background sync, not a user action.
      if (resp.status === 401) cloudSignOut();
      throw new Error((payload && payload.error) || ("Append failed (" + resp.status + ")"));
    }
    markSynced(design.id);
    return payload;
  }

  /**
   * Pull the cloud library for the current token and merge into local
   * storage. Used on /pages/my-designs to show designs from another device.
   * Returns { ok, designs, merged: N }.
   */
  async function cloudPull() {
    if (!cloudWorkerUrl) throw new Error("Cloud library not configured.");
    const token = cloudGetToken();
    if (!token) throw new Error("Not signed in.");
    const resp = await fetch(cloudWorkerUrl + "/api/library/list?token=" + encodeURIComponent(token));
    let payload = null;
    try { payload = await resp.json(); } catch (_) { payload = null; }
    if (!resp.ok || !payload || !payload.ok) {
      if (resp.status === 401) cloudSignOut();
      throw new Error((payload && payload.error) || ("List failed (" + resp.status + ")"));
    }
    if (payload.email) {
      try { localStorage.setItem(EMAIL_KEY, String(payload.email)); } catch (_) {}
    }
    // Merge cloud designs into local, de-duping by id. Cloud is the source
    // of truth for shared identity, local wins on any field we don't replace.
    const state = read();
    const localById = {};
    state.designs.forEach(function (d) { localById[d.id] = d; });
    let merged = 0;
    (payload.designs || []).forEach(function (d) {
      if (!d || !d.id) return;
      if (localById[d.id]) {
        // Already have it locally — refresh lastUsedAt if cloud is newer.
        if ((d.lastUsedAt || 0) > (localById[d.id].lastUsedAt || 0)) {
          localById[d.id].lastUsedAt = d.lastUsedAt;
        }
        return;
      }
      state.designs.push({
        id: d.id,
        prompt: d.prompt || "",
        draftUrl: d.draftUrl,
        thumbnail: d.thumbnail || "",
        createdAt: d.createdAt || Date.now(),
        lastUsedAt: d.lastUsedAt || d.createdAt || Date.now(),
      });
      markSynced(d.id);
      merged++;
    });
    // Honour cap on merge — keep newest if we overflow.
    if (state.designs.length > MAX_DESIGNS) {
      state.designs.sort(function (a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
      state.designs = state.designs.slice(0, MAX_DESIGNS);
    }
    write(state);
    return { ok: true, designs: payload.designs || [], merged: merged };
  }

  /**
   * Ask the worker to re-send the magic-link email. Never reveals whether the
   * email is in the system — always returns the same shape.
   */
  async function cloudRecover(email) {
    if (!cloudWorkerUrl) throw new Error("Cloud library not configured.");
    const resp = await fetch(cloudWorkerUrl + "/api/library/recover", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email }),
    });
    let payload = null;
    try { payload = await resp.json(); } catch (_) { payload = null; }
    if (!resp.ok) {
      throw new Error((payload && payload.error) || ("Recover failed (" + resp.status + ")"));
    }
    return payload || { ok: true, sent: true };
  }

  /**
   * Read ?lib=<token> from the current URL. If present, bind it locally,
   * clean it out of the URL (so refreshes don't re-trigger), and trigger
   * a pull if a worker URL has been registered.
   *
   * Runs once, on script load. Safe to call twice — second call is a no-op
   * because the param has been stripped.
   */
  function handleMagicLinkInUrl() {
    try {
      const params = new URLSearchParams(global.location.search);
      const token = params.get("lib");
      if (!token) return;
      // Bind first so subsequent page navigations stay authenticated.
      cloudBind("", token);
      // Strip the param from the URL — keeps it out of analytics + prevents
      // accidental re-share. We don't replace the path, just the query.
      params.delete("lib");
      const newQuery = params.toString();
      const newUrl = global.location.pathname +
        (newQuery ? "?" + newQuery : "") +
        global.location.hash;
      try { global.history.replaceState({}, "", newUrl); } catch (_) {}
      // Pull cloud library if the worker URL is registered yet. If not,
      // the consumer page (my-designs.js) will call cloudPull() once it
      // has set the worker URL.
      if (cloudWorkerUrl) {
        cloudPull().catch(function (e) {
          console.warn("Magic-link pull failed", e);
        });
      }
    } catch (e) {
      console.warn("handleMagicLinkInUrl failed", e);
    }
  }

  // Banner state (designer page email-capture prompt) — small helpers so
  // ai-designer.js doesn't have to know about the storage key.
  function bannerDismissed() {
    try { return localStorage.getItem(BANNER_KEY) === "dismissed"; } catch (_) { return false; }
  }
  function bannerSubscribed() {
    try { return localStorage.getItem(BANNER_KEY) === "subscribed"; } catch (_) { return false; }
  }
  function markBannerDismissed() {
    try { localStorage.setItem(BANNER_KEY, "dismissed"); } catch (_) {}
  }
  function markBannerSubscribed() {
    try { localStorage.setItem(BANNER_KEY, "subscribed"); } catch (_) {}
  }

  global.AIDesignLibrary = {
    _v: 2, // bumped when this file's public API changes — read by the
           // double-load guard at the top of the IIFE
    list: list,
    get: get,
    save: save,
    remove: remove,
    touch: touch,
    clear: clear,
    cloud: {
      setWorkerUrl: cloudSetWorkerUrl,
      getWorkerUrl: cloudGetWorkerUrl,
      getToken: cloudGetToken,
      getEmail: cloudGetEmail,
      isSignedIn: cloudIsSignedIn,
      bind: cloudBind,
      signOut: cloudSignOut,
      save: cloudSave,
      append: cloudAppend,
      pull: cloudPull,
      recover: cloudRecover,
      handleMagicLinkInUrl: handleMagicLinkInUrl,
    },
    banner: {
      dismissed: bannerDismissed,
      subscribed: bannerSubscribed,
      markDismissed: markBannerDismissed,
      markSubscribed: markBannerSubscribed,
    },
  };

  // Auto-run magic-link detection on script load. The pull may not fire
  // until the consumer page calls cloud.setWorkerUrl() — that's fine,
  // the consumer is responsible for calling pull() once URL is set.
  handleMagicLinkInUrl();

  // ============= Cross-origin blob fetch (XHR-based) =============
  /**
   * Fetch a remote URL as a Blob, using XMLHttpRequest instead of window.fetch.
   * This is the storefront's third-party-script-proof way to download files
   * — Shopify's pixel scripts wrap window.fetch and break cross-origin
   * responses, but XHR is left alone.
   */
  function fetchBlob(url, timeoutMs) {
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "blob";
      xhr.timeout = timeoutMs || 60000;
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
        else reject(new Error("HTTP " + xhr.status));
      };
      xhr.onerror = function () { reject(new Error("Network error")); };
      xhr.ontimeout = function () { reject(new Error("Timed out")); };
      xhr.send();
    });
  }

  global.AIDesignFetch = { fetchBlob: fetchBlob };

  // ============= Branded share image composer =============
  /**
   * Compose a branded share image: original design on top, black footer
   * strip below with "60FT INK · 60ftink.com.au". Returns a Blob.
   *
   * The footer continues the black background of the design so it looks
   * intentional, not stuck-on. Marketing payload is baked into the pixels
   * so it survives any sharing pipeline (IG Story, Messages, FB, anything).
   */
  function composeBrandedImage(originalBlob, opts) {
    opts = opts || {};
    const brandName = opts.brandName || "60FT INK";
    const tagline = opts.tagline || "AI Design · 60ftink.com.au";

    return new Promise(function (resolve, reject) {
      const objectUrl = URL.createObjectURL(originalBlob);
      const img = new Image();
      img.onload = function () {
        try {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          // Footer ~12% of the design height. On 1024px = ~123px.
          const footerH = Math.round(h * 0.12);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h + footerH;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas not supported"));

          // Solid black background (matches the design's black bg seamlessly)
          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          // Subtle horizontal divider line
          ctx.fillStyle = "rgba(255,255,255,0.10)";
          ctx.fillRect(0, h, w, 1);

          // Brand name (bold, large)
          const brandSize = Math.round(footerH * 0.32);
          ctx.fillStyle = "#ffffff";
          ctx.font = "700 " + brandSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(brandName, w / 2, h + footerH * 0.4);

          // Tagline (smaller, muted)
          const taglineSize = Math.round(footerH * 0.18);
          ctx.fillStyle = "#94a3b8";
          ctx.font = "500 " + taglineSize + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
          ctx.fillText(tagline, w / 2, h + footerH * 0.72);

          URL.revokeObjectURL(objectUrl);
          canvas.toBlob(function (blob) {
            if (blob) resolve(blob);
            else reject(new Error("Couldn't compose branded image"));
          }, "image/png");
        } catch (e) {
          URL.revokeObjectURL(objectUrl);
          reject(e);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Couldn't decode image for branding"));
      };
      img.src = objectUrl;
    });
  }

  // ============= Share helpers =============
  /**
   * Share a design via the Web Share API. On mobile this opens the native
   * share sheet (Instagram, Messages, WhatsApp, etc.) with the image file
   * attached. On desktop or older browsers, falls back to URL share or
   * clipboard copy with a toast notification.
   *
   * Default share text drives traffic back to the store.
   */
  async function shareDesign(opts) {
    const url = (opts && opts.url) || "";
    const prompt = (opts && opts.prompt) || "design";
    const storeUrl = (opts && opts.storeUrl) || "https://60ftink.com.au";
    const title = "My AI design from 60ft Ink";
    const text =
      (opts && opts.text) ||
      'Made this in 30 seconds with the AI designer at ' + storeUrl + ' — try it yourself!';

    if (!url) {
      showToast("Couldn't share — design URL missing.");
      return;
    }

    // Attempt 1 — share with the BRANDED image file (best mobile UX +
    // baked-in marketing). We add a "60FT INK · 60ftink.com.au" footer
    // strip so every share carries the store branding in the pixels —
    // survives any social pipeline (IG Story, Messages, FB, etc.)
    if (navigator.canShare && navigator.share) {
      try {
        const rawBlob = await fetchBlob(url);
        let shareBlob = rawBlob;
        try {
          shareBlob = await composeBrandedImage(rawBlob);
        } catch (compErr) {
          // If branding fails for any reason, fall back to raw image
          console.warn("Branding failed, sharing raw image", compErr);
        }
        const file = new File([shareBlob], "60ft-Ink-AI-Design.png", {
          type: "image/png",
        });
        const shareData = { title: title, text: text, files: [file] };
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      } catch (e) {
        if (e && e.name === "AbortError") return; // user cancelled
        console.warn("File share failed, falling back", e);
      }
    }

    // Attempt 2 — URL share (no file)
    if (navigator.share) {
      try {
        await navigator.share({ title: title, text: text, url: url });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
        console.warn("URL share failed, falling back to clipboard", e);
      }
    }

    // Attempt 3 — clipboard fallback
    try {
      await navigator.clipboard.writeText(text + "\n" + url);
      showToast("Link copied to clipboard ✓");
    } catch (e) {
      window.prompt("Copy this link to share:", url);
    }
  }

  /** Lightweight toast notification — appears, fades, removes itself. */
  function showToast(message, durationMs) {
    durationMs = durationMs || 2200;
    let host = document.getElementById("ai-design-toast-host");
    if (!host) {
      host = document.createElement("div");
      host.id = "ai-design-toast-host";
      host.style.cssText = [
        "position:fixed",
        "bottom:24px",
        "left:50%",
        "transform:translateX(-50%)",
        "z-index:99999",
        "display:flex",
        "flex-direction:column",
        "gap:8px",
        "pointer-events:none",
      ].join(";");
      document.body.appendChild(host);
    }
    const toast = document.createElement("div");
    toast.textContent = message;
    toast.style.cssText = [
      "background:rgba(15,23,42,0.92)",
      "color:#ffffff",
      "padding:10px 16px",
      "border-radius:10px",
      "font:14px -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
      "box-shadow:0 8px 24px rgba(15,23,42,0.18)",
      "pointer-events:auto",
      "transition:opacity 0.2s ease, transform 0.2s ease",
      "opacity:0",
      "transform:translateY(8px)",
    ].join(";");
    host.appendChild(toast);
    requestAnimationFrame(function () {
      toast.style.opacity = "1";
      toast.style.transform = "translateY(0)";
    });
    setTimeout(function () {
      toast.style.opacity = "0";
      toast.style.transform = "translateY(8px)";
      setTimeout(function () { toast.remove(); }, 250);
    }, durationMs);
  }

  global.AIDesignShare = {
    share: shareDesign,
    toast: showToast,
  };
})(window);